import { useEffect, useState } from 'react';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { firestore, FIRESTORE_COLLECTIONS } from '../firebase';
import { getDayVibeDateKey } from './dayVibeDate';
import { resolvePartnerIdentity } from './partnerIdentity';

// The only personal match percentage Tonyte shows: the Day Vibe score the
// server's matching pipeline (functions/index.js processDayVibePair) stored
// for exactly this pair in the current round. The server only scores two
// answer sheets from the same round and questionnaire version, and only
// writes pairs scoring ≥ 70%, so "no record" means "no score to show" —
// never a fallback number.

const CACHE_TTL_MS = 5 * 60 * 1000;
const roundCache = new Map<string, { scores: Map<string, number>; at: number }>();
const roundPending = new Map<string, Promise<Map<string, number>>>();

export function dayVibeMatchId(dateKey: string, uidA: string, uidB: string) {
  const [minUid, maxUid] = uidA < uidB ? [uidA, uidB] : [uidB, uidA];
  return `${dateKey}_${minUid}__${maxUid}`;
}

/** Validated score from a dayVibeMatches record, or null if it isn't a usable score for this pair and round. */
export function readPairScore(
  docId: string,
  data: Record<string, unknown>,
  viewerUid: string,
  otherUid: string,
  dateKey: string,
): number | null {
  if (docId !== dayVibeMatchId(dateKey, viewerUid, otherUid)) return null;
  if (data.dateKey !== dateKey) return null;
  const users = Array.isArray(data.users) ? data.users : [];
  if (users.length !== 2 || !users.includes(viewerUid) || !users.includes(otherUid)) return null;
  const percent = data.matchPercent;
  if (typeof percent !== 'number' || !Number.isFinite(percent) || percent < 0 || percent > 100) return null;
  return Math.round(percent);
}

// One query per viewer per round (the same indexed query Vibe Circle uses),
// shared by every profile/list that asks within the cache window.
async function loadRoundScores(viewerUid: string, dateKey: string): Promise<Map<string, number>> {
  const key = `${viewerUid}_${dateKey}`;
  const cached = roundCache.get(key);
  if (cached && Date.now() - cached.at < CACHE_TTL_MS) return cached.scores;
  const inFlight = roundPending.get(key);
  if (inFlight) return inFlight;

  const promise = getDocs(query(
    collection(firestore, FIRESTORE_COLLECTIONS.dayVibeMatches),
    where('users', 'array-contains', viewerUid),
    where('dateKey', '==', dateKey),
    orderBy('matchPercent', 'desc'),
  )).then(snapshot => {
    const scores = new Map<string, number>();
    snapshot.docs.forEach(item => {
      const data = item.data();
      const users = Array.isArray(data.users) ? data.users : [];
      const otherUid = users.find(uid => uid !== viewerUid);
      if (typeof otherUid !== 'string') return;
      const score = readPairScore(item.id, data, viewerUid, otherUid, dateKey);
      if (score !== null) scores.set(otherUid, score);
    });
    roundCache.set(key, { scores, at: Date.now() });
    return scores;
  }).finally(() => roundPending.delete(key));

  roundPending.set(key, promise);
  return promise;
}

/** Real Vibe Game score for this pair this round, or null whenever it can't be shown. */
export async function getPairVibeGameScore(viewerUid: string | null | undefined, otherUid: string | null | undefined): Promise<number | null> {
  if (!viewerUid || !otherUid || viewerUid === otherUid) return null;
  try {
    // Partner accounts never get a personal compatibility percentage.
    const [viewer, other] = await Promise.all([resolvePartnerIdentity(viewerUid), resolvePartnerIdentity(otherUid)]);
    if (viewer.kind === 'partner' || other.kind === 'partner') return null;
    const scores = await loadRoundScores(viewerUid, getDayVibeDateKey());
    return scores.get(otherUid) ?? null;
  } catch (error) {
    console.warn('[Tonyte][RN][VibeGameScore] unavailable', { otherUid, error });
    return null;
  }
}

export function usePairVibeGameScore(viewerUid: string | null | undefined, otherUid: string | null | undefined): number | null {
  const [score, setScore] = useState<number | null>(null);
  useEffect(() => {
    let active = true;
    setScore(null);
    void getPairVibeGameScore(viewerUid, otherUid).then(value => { if (active) setScore(value); });
    return () => { active = false; };
  }, [otherUid, viewerUid]);
  return score;
}
